import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "mb-16",
    "aria-roledescription": "carousel",
    "aria-label": "Menüs"
};
const _hoisted_2 = { class: "relative" };
const _hoisted_3 = ["aria-label", "aria-hidden"];
const _hoisted_4 = ["disabled"];
const _hoisted_5 = ["disabled"];
const _hoisted_6 = {
    key: 0,
    class: "mt-6 flex justify-center gap-2"
};
const _hoisted_7 = ["aria-label", "aria-current", "onClick"];
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import MenuCard from './MenuCard.vue';
const AUTOPLAY_MS = 5000;
const SWIPE_THRESHOLD = 40;
export default /*@__PURE__*/ _defineComponent({
    __name: 'MenuSlider',
    props: {
        menus: {},
        imagePosition: { default: 'above' },
        imageMaxHeightPercent: { default: 100 }
    },
    emits: ["select-menu"],
    setup(__props) {
        const props = __props;
        const currentSlide = ref(0);
        const itemsPerSlide = ref(1);
        const isPaused = ref(false);
        const autoplayTimer = ref(null);
        const touchStartX = ref(0);
        const slides = computed(() => {
            const perSlide = Math.max(itemsPerSlide.value, 1);
            const grouped = [];
            for (let i = 0; i < props.menus.length; i += perSlide) {
                grouped.push(props.menus.slice(i, i + perSlide));
            }
            return grouped;
        });
        const totalSlides = computed(() => slides.value.length);
        // Keine Steuerung/Autoplay, wenn alles auf einen Slide passt.
        const hasControls = computed(() => totalSlides.value > 1);
        const slideGridClass = computed(() => {
            switch (itemsPerSlide.value) {
                case 3:
                    return 'grid-cols-3';
                case 2:
                    return 'grid-cols-2';
                default:
                    return 'grid-cols-1';
            }
        });
        const trackStyle = computed(() => ({
            transform: `translateX(-${currentSlide.value * 100}%)`
        }));
        const updateItemsPerSlide = () => {
            if (typeof window === 'undefined') {
                itemsPerSlide.value = 1;
                return;
            }
            const width = window.innerWidth;
            if (width >= 1024)
                itemsPerSlide.value = 3;
            else if (width >= 768)
                itemsPerSlide.value = 2;
            else
                itemsPerSlide.value = 1;
        };
        const clampCurrentSlide = () => {
            if (totalSlides.value === 0) {
                currentSlide.value = 0;
            }
            else if (currentSlide.value > totalSlides.value - 1) {
                currentSlide.value = totalSlides.value - 1;
            }
        };
        const stopAutoplay = () => {
            if (autoplayTimer.value) {
                clearInterval(autoplayTimer.value);
                autoplayTimer.value = null;
            }
        };
        const startAutoplay = () => {
            stopAutoplay();
            if (!hasControls.value || isPaused.value)
                return;
            autoplayTimer.value = setInterval(() => {
                currentSlide.value = currentSlide.value >= totalSlides.value - 1 ? 0 : currentSlide.value + 1;
            }, AUTOPLAY_MS);
        };
        const pauseAutoplay = () => {
            isPaused.value = true;
            stopAutoplay();
        };
        const resumeAutoplay = () => {
            isPaused.value = false;
            startAutoplay();
        };
        const restartAutoplay = () => {
            if (!isPaused.value)
                startAutoplay();
        };
        const nextSlide = () => {
            if (currentSlide.value < totalSlides.value - 1) {
                currentSlide.value += 1;
                restartAutoplay();
            }
        };
        const prevSlide = () => {
            if (currentSlide.value > 0) {
                currentSlide.value -= 1;
                restartAutoplay();
            }
        };
        const goToSlide = (index) => {
            if (index >= 0 && index < totalSlides.value) {
                currentSlide.value = index;
                restartAutoplay();
            }
        };
        const onTouchStart = (event) => {
            touchStartX.value = event.changedTouches[0]?.clientX ?? 0;
        };
        const onTouchEnd = (event) => {
            const endX = event.changedTouches[0]?.clientX ?? 0;
            const delta = endX - touchStartX.value;
            if (Math.abs(delta) < SWIPE_THRESHOLD)
                return;
            if (delta < 0)
                nextSlide();
            else
                prevSlide();
        };
        const handleResize = () => {
            updateItemsPerSlide();
            clampCurrentSlide();
        };
        watch(() => props.menus.length, () => {
            clampCurrentSlide();
            startAutoplay();
        });
        watch(totalSlides, () => {
            clampCurrentSlide();
            startAutoplay();
        });
        onMounted(() => {
            updateItemsPerSlide();
            startAutoplay();
            if (typeof window !== 'undefined') {
                window.addEventListener('resize', handleResize);
            }
        });
        onBeforeUnmount(() => {
            stopAutoplay();
            if (typeof window !== 'undefined') {
                window.removeEventListener('resize', handleResize);
            }
        });
        return (_ctx, _cache) => {
            return (_ctx.menus.length)
                ? (_openBlock(), _createElementBlock("section", _hoisted_1, [
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "mb-6" }, [
                        _createElementVNode("h2", { class: "text-3xl lg:text-4xl font-bold text-gray-900 mb-2 flex items-center" }, [
                            _createElementVNode("i", { class: "fas fa-layer-group mr-4 text-red-500" }),
                            _createTextVNode(" Menüs ")
                        ]),
                        _createElementVNode("p", { class: "text-gray-600 text-lg" }, "Sparen Sie mit unseren Kombi-Menüs")
                    ], -1 /* CACHED */)),
                    _createElementVNode("div", _hoisted_2, [
                        _createCommentVNode(" Viewport "),
                        _createElementVNode("div", {
                            class: "overflow-hidden py-2",
                            onMouseenter: pauseAutoplay,
                            onMouseleave: resumeAutoplay,
                            onFocusin: pauseAutoplay,
                            onFocusout: resumeAutoplay,
                            onTouchstartPassive: onTouchStart,
                            onTouchendPassive: onTouchEnd
                        }, [
                            _createElementVNode("div", {
                                class: "flex transition-transform duration-500 ease-out",
                                style: _normalizeStyle(trackStyle.value)
                            }, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (slideMenus, slideIndex) => {
                                    return (_openBlock(), _createElementBlock("div", {
                                        key: `menu-slide-${slideIndex}`,
                                        class: _normalizeClass(["grid min-w-full gap-6 px-1", slideGridClass.value]),
                                        role: "group",
                                        "aria-label": `Slide ${slideIndex + 1} von ${totalSlides.value}`,
                                        "aria-hidden": slideIndex !== currentSlide.value
                                    }, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slideMenus, (menu) => {
                                            return (_openBlock(), _createBlock(MenuCard, {
                                                key: menu.id,
                                                menu: menu,
                                                "image-position": _ctx.imagePosition,
                                                "image-max-height-percent": _ctx.imageMaxHeightPercent,
                                                onSelect: ($event) => (_ctx.$emit('select-menu', menu))
                                            }, null, 8 /* PROPS */, ["menu", "image-position", "image-max-height-percent", "onSelect"]));
                                        }), 128 /* KEYED_FRAGMENT */))
                                    ], 10 /* CLASS, PROPS */, _hoisted_3));
                                }), 128 /* KEYED_FRAGMENT */))
                            ], 4 /* STYLE */)
                        ], 32 /* NEED_HYDRATION */),
                        _createCommentVNode(" Pfeile (nur wenn mehr als ein Slide) "),
                        (hasControls.value)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "absolute -left-2 lg:-left-4 top-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-2 shadow-md transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed",
                                    "aria-label": "Vorheriges Menü",
                                    disabled: currentSlide.value === 0,
                                    onClick: prevSlide
                                }, [...(_cache[0] || (_cache[0] = [
                                        _createElementVNode("svg", {
                                            class: "h-6 w-6 text-gray-800",
                                            fill: "none",
                                            stroke: "currentColor",
                                            viewBox: "0 0 24 24"
                                        }, [
                                            _createElementVNode("path", {
                                                "stroke-linecap": "round",
                                                "stroke-linejoin": "round",
                                                "stroke-width": "2",
                                                d: "M15 19l-7-7 7-7"
                                            })
                                        ], -1 /* CACHED */)
                                    ]))], 8 /* PROPS */, _hoisted_4),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "absolute -right-2 lg:-right-4 top-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-2 shadow-md transition-colors hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed",
                                    "aria-label": "Nächstes Menü",
                                    disabled: currentSlide.value >= totalSlides.value - 1,
                                    onClick: nextSlide
                                }, [...(_cache[1] || (_cache[1] = [
                                        _createElementVNode("svg", {
                                            class: "h-6 w-6 text-gray-800",
                                            fill: "none",
                                            stroke: "currentColor",
                                            viewBox: "0 0 24 24"
                                        }, [
                                            _createElementVNode("path", {
                                                "stroke-linecap": "round",
                                                "stroke-linejoin": "round",
                                                "stroke-width": "2",
                                                d: "M9 5l7 7-7 7"
                                            })
                                        ], -1 /* CACHED */)
                                    ]))], 8 /* PROPS */, _hoisted_5)
                            ], 64 /* STABLE_FRAGMENT */))
                            : _createCommentVNode("v-if", true)
                    ]),
                    _createCommentVNode(" Punkte "),
                    (hasControls.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (_, index) => {
                                return (_openBlock(), _createElementBlock("button", {
                                    key: `menu-dot-${index}`,
                                    type: "button",
                                    class: _normalizeClass(["h-2 rounded-full transition-all duration-300", currentSlide.value === index ? 'bg-red-600 w-8' : 'bg-gray-300 w-2']),
                                    "aria-label": `Gehe zu Slide ${index + 1}`,
                                    "aria-current": currentSlide.value === index,
                                    onClick: ($event) => (goToSlide(index))
                                }, null, 10 /* CLASS, PROPS */, _hoisted_7));
                            }), 128 /* KEYED_FRAGMENT */))
                        ]))
                        : _createCommentVNode("v-if", true)
                ]))
                : _createCommentVNode("v-if", true);
        };
    }
});
